import React, { useState, useEffect, useRef } from 'react';
import {
    RefreshCw, LayoutDashboard, TrendingUp, Wallet, Coins, Landmark,
    Home, MapPin, Target, Percent, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchPortfolioAnalytics } from '../api/analyticsApi';

// Compact money: $3.84M / $654K / $580
const fmtCompact = (n) => {
    if (n === null || n === undefined || isNaN(n)) return '—';
    const v = Number(n);
    const sign = v < 0 ? '-' : '';
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
    return `${sign}$${Math.round(abs).toLocaleString()}`;
};
const fmtMoney = (n, signed = false) => {
    if (n === null || n === undefined || isNaN(n)) return '—';
    const v = Math.round(Number(n));
    const sign = signed && v > 0 ? '+' : v < 0 ? '-' : '';
    return `${sign}$${Math.abs(v).toLocaleString()}`;
};
const fmtPct = (n) => (n === null || n === undefined || isNaN(n) ? 'N/A' : `${Number(n).toFixed(1)}%`);
const fmtNum = (n) => (n || 0).toLocaleString();

const Kpi = ({ label, value, sub, tone, Icon, accent = '#2563eb', onClick }) => {
    const inner = (
        <>
            <div className="flex items-center gap-2 mb-2">
                {Icon && (
                    <span className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ background: `${accent}1a`, color: accent }}>
                        <Icon className="w-4 h-4" />
                    </span>
                )}
                <span className="text-xs font-medium text-gray-500">{label}</span>
            </div>
            <div className={`text-2xl font-bold ${tone === 'g' ? 'text-emerald-600' : tone === 'r' ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
            {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
        </>
    );
    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                title="View matching investors"
                className="text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-[#2563eb] hover:shadow-sm transition-all cursor-pointer"
            >
                {inner}
            </button>
        );
    }
    return <div className="bg-white rounded-xl border border-gray-200 p-4">{inner}</div>;
};

const Card = ({ title, Icon, iconColor = '#2563eb', children, subtitle }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: iconColor }} /> {title}
        </h3>
        {subtitle && <p className="text-xs text-gray-400 mb-3">{subtitle}</p>}
        <div className={subtitle ? '' : 'mt-3'}>{children}</div>
    </div>
);

// Horizontal bar list — [{label, count, value?}]. Shares a max for proportional bars.
const BarList = ({ rows, accent = '#2563eb', showValue = true, empty = 'No data yet', onRowClick }) => {
    if (!rows || rows.length === 0) return <p className="text-xs text-gray-400 italic">{empty}</p>;
    const max = Math.max(1, ...rows.map((r) => r.count || 0));
    return (
        <div className="space-y-2.5">
            {rows.map((r) => {
                const clickable = onRowClick && (r.count || 0) > 0;
                return (
                    <div
                        key={r.key || r.label || r.suburb}
                        onClick={clickable ? () => onRowClick(r) : undefined}
                        title={clickable ? 'View matching investors' : undefined}
                        className={clickable ? 'group cursor-pointer rounded-md -mx-1 px-1 py-0.5 hover:bg-gray-50' : undefined}
                    >
                        <div className="flex items-center justify-between text-xs mb-1">
                            <span className={`text-gray-700 ${clickable ? 'group-hover:text-[#2563eb] group-hover:font-medium' : ''}`}>{r.label || r.suburb}</span>
                            <span className="text-gray-400 tabular-nums shrink-0 ml-2">
                                {fmtNum(r.count)}{showValue && r.value ? ` · ${fmtCompact(r.value)}` : ''}
                            </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full transition-all"
                                 style={{ width: `${Math.round(((r.count || 0) / max) * 100)}%`, background: accent, minWidth: r.count > 0 ? 4 : 0 }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// `onDrill(filters, label)` — when provided, headline flag cards and every
// breakdown row become clickable and drill into the matching investor list.
const PortfolioAnalyticsPanel = ({ startDate, endDate, onDrill }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const tokenRef = useRef(0);
    const drill = onDrill || (() => {});

    useEffect(() => {
        const myToken = ++tokenRef.current;
        setLoading(true);
        fetchPortfolioAnalytics(startDate, endDate)
            .then((d) => { if (myToken === tokenRef.current) setData(d); })
            .catch((err) => {
                if (myToken !== tokenRef.current) return;
                console.error(err);
                toast.error('Failed to load portfolio analytics');
            })
            .finally(() => { if (myToken === tokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading portfolio analytics…
            </div>
        );
    }

    const t = data?.totals || {};

    if (!t.properties) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                <LayoutDashboard className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">No investor portfolios yet</p>
                <p className="text-xs text-gray-400 mt-1">
                    Numbers will appear here once buyers start adding properties to the Portfolio Tracker.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
                <LayoutDashboard className="w-4 h-4 text-gray-400" />
                <span>
                    <strong className="text-gray-900">{fmtNum(t.investors)}</strong> investor{t.investors === 1 ? '' : 's'} tracking{' '}
                    <strong className="text-gray-900">{fmtNum(t.properties)}</strong> propert{t.properties === 1 ? 'y' : 'ies'}
                    {' '}· avg {t.avg_properties_per_investor} per investor
                    {t.added_in_range ? ` · ${fmtNum(t.added_in_range)} added this period` : ''}
                </span>
            </div>

            {/* Headline portfolio KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Portfolio value" value={fmtCompact(t.portfolio_value)} Icon={Home} accent="#2563eb" onClick={onDrill ? () => drill({ sort: 'portfolio_value' }, 'Ranked by portfolio value') : undefined} />
                <Kpi label="Total equity" value={fmtCompact(t.total_equity)} sub={t.avg_lvr != null ? `Avg LVR ${t.avg_lvr}%` : null} Icon={Wallet} accent="#0e9f6e" onClick={onDrill ? () => drill({ sort: 'total_equity' }, 'Ranked by total equity') : undefined} />
                <Kpi label="Annual cashflow" tone={t.annual_cashflow >= 0 ? 'g' : 'r'} value={fmtMoney(t.annual_cashflow, true)} sub={`${fmtMoney(t.monthly_cashflow, true)}/mo`} Icon={Coins} accent="#d97706" onClick={onDrill ? () => drill({ sort: 'monthly_cashflow' }, 'Ranked by cashflow') : undefined} />
                <Kpi label="Avg net yield" value={fmtPct(t.avg_net_yield)} sub={`Gross ${fmtPct(t.avg_gross_yield)}`} Icon={Percent} accent="#7c3aed" onClick={onDrill ? () => drill({ sort: 'avg_net_yield' }, 'Ranked by net yield') : undefined} />
            </div>

            {/* Loan / debt profile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Total debt" value={fmtCompact(t.total_debt)} sub={`${fmtNum(t.with_loan)} with a loan · ${fmtNum(t.owned_outright)} outright`} Icon={Landmark} accent="#dc2626" onClick={onDrill ? () => drill({ sort: 'total_debt' }, 'Ranked by total debt') : undefined} />
                <Kpi label="Avg interest rate" value={t.avg_interest_rate != null ? `${t.avg_interest_rate}%` : 'N/A'} sub={`${fmtMoney(t.monthly_repayments)}/mo repayments`} Icon={Percent} accent="#0891b2" />
                <Kpi label="Accessible equity" tone="g" value={fmtCompact(t.accessible_equity)} sub="at 80% LVR · ranked" Icon={TrendingUp} accent="#0e9f6e" onClick={onDrill ? () => drill({ sort: 'accessible_equity' }, 'Ranked by accessible equity') : undefined} />
                <Kpi label="Gross rent" value={fmtMoney(t.annual_rent)} sub={`Expenses ${fmtMoney(t.annual_expenses)}`} Icon={Coins} accent="#2563eb" onClick={onDrill ? () => drill({ sort: 'annual_rent' }, 'Ranked by gross rent') : undefined} />
            </div>

            {/* Risk-flag strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Cashflow negative" tone={t.cashflow_negative ? 'r' : undefined} value={fmtNum(t.cashflow_negative)} sub="investors" Icon={AlertTriangle} accent="#dc2626" onClick={onDrill ? () => drill({ cashflow: 'negative' }, 'Cashflow-negative investors') : undefined} />
                <Kpi label="Negatively geared" value={fmtNum(t.negatively_geared)} sub="self-reported" Icon={AlertTriangle} accent="#d97706" />
                <Kpi label="Fixed expiring soon" tone={t.fixed_expiring_soon ? 'r' : undefined} value={fmtNum(t.fixed_expiring_soon)} sub="within 120 days" Icon={AlertTriangle} accent="#d97706" onClick={onDrill ? () => drill({ fixed_expiring: true }, 'Fixed loan expiring soon') : undefined} />
                <Kpi label="Professionally managed" value={fmtNum(t.managed)} sub={`${fmtNum(t.self_managed)} self-managed`} Icon={Home} accent="#0891b2" onClick={onDrill ? () => drill({ managed: 'managed' }, 'Professionally managed') : undefined} />
            </div>

            {/* Breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="By property type" Icon={Home} iconColor="#2563eb" subtitle="Count of tracked properties · total value">
                    <BarList rows={data.by_property_type} accent="#2563eb" onRowClick={onDrill ? (r) => drill({ property_type: r.key }, `Property type: ${r.label}`) : undefined} />
                </Card>
                <Card title="By state" Icon={MapPin} iconColor="#0e9f6e" subtitle="Where investors are buying">
                    <BarList rows={data.by_state} accent="#0e9f6e" onRowClick={onDrill ? (r) => drill({ state: r.key }, `State: ${r.label}`) : undefined} />
                </Card>
                <Card title="By ownership entity" Icon={Landmark} iconColor="#7c3aed" subtitle="How properties are held">
                    <BarList rows={data.by_ownership_entity} accent="#7c3aed" onRowClick={onDrill ? (r) => drill({ entity: r.key }, `Ownership: ${r.label}`) : undefined} />
                </Card>
                <Card title="By property status" Icon={Home} iconColor="#d97706" subtitle="Investment / owner-occupied / vacant">
                    <BarList rows={data.by_status} accent="#d97706" onRowClick={onDrill ? (r) => drill({ status: r.key }, `Status: ${r.label}`) : undefined} />
                </Card>
            </div>

            {/* Investor strategy */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card title="Investment goals" Icon={Target} iconColor="#db2777" subtitle={`${fmtNum(t.investors_with_goals)} investors set goals`}>
                    <BarList rows={data.goals} accent="#db2777" showValue={false} empty="No goals set yet" onRowClick={onDrill ? (r) => drill({ goal: r.key }, `Goal: ${r.label}`) : undefined} />
                </Card>
                <Card title="Time horizon" Icon={TrendingUp} iconColor="#0891b2">
                    <BarList rows={data.horizons} accent="#0891b2" showValue={false} onRowClick={onDrill ? (r) => drill({ horizon: r.key }, `Time horizon: ${r.label}`) : undefined} />
                </Card>
                <Card title="Risk appetite" Icon={AlertTriangle} iconColor="#d97706">
                    <BarList rows={data.risks} accent="#d97706" showValue={false} onRowClick={onDrill ? (r) => drill({ risk: r.key }, `Risk appetite: ${r.label}`) : undefined} />
                </Card>
            </div>

            {/* Top suburbs */}
            <Card title="Top investor suburbs" Icon={MapPin} iconColor="#2563eb" subtitle="Most-held suburbs across all portfolios · total value">
                <BarList rows={data.top_suburbs} accent="#2563eb" onRowClick={onDrill ? (r) => drill({ suburb: r.suburb }, `Suburb: ${r.suburb}`) : undefined} />
            </Card>
        </div>
    );
};

export default PortfolioAnalyticsPanel;
